import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { UserInactivityMonitorService } from '@crt/sui';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { AppConstants } from '@app/app.constants.service';
import { UserState } from '@app/auth/user.state';
import { BootstrapSuffix } from '@app/common.model';
import { InactivityConfig } from '@app/common.model';
import { BrowserNotificationService } from '@app/shared/browser-notification.service';
import { CanUseService, Feature } from '@app/shared/can-use.service';
import { InactivityWarningModalComponent } from '@app/shared/inactivity-warning-modal/inactivity-warning-modal.component';
import { Toast } from '@app/toasts/toast.model';
import { ToastService } from '@app/toasts/toast.service';

@Injectable({ providedIn: 'root' })
export class InactivityDetectionService {
  private readonly browserNotifications = inject(BrowserNotificationService);
  private readonly canUseService = inject(CanUseService);
  private readonly inactivityMonitor = inject(UserInactivityMonitorService);
  private readonly ngbModal = inject(NgbModal);
  private readonly toastService = inject(ToastService);
  private readonly userState = inject(UserState);

  /** BroadcastChannel for cross-tab dismiss communication */
  private readonly broadcastChannel =
    typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('inactivity-modal-dismiss')
      : null;

  /** BroadcastChannel for tracking modal show/hide across tabs */
  private readonly trackingChannel =
    typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('inactivity-modal-tracking')
      : null;

  private modalRef: null | NgbModalRef = null;

  /** Count of other tabs with active warnings */
  private readonly otherTabsWarningCount = signal<number>(0);

  /** Reference to the active toast showing warning count */
  private warningCountToast: null | Toast = null;

  /** Excule super and provisioning admin from inactivity warnings. */
  private readonly excludeFromInactivityWarnings = computed(() => {
    const user = this.userState.user();
    return !!(user?.superAdmin || user?.provisioningAdmin);
  });

  /** Check if `Feature.InactivityTimeout` is enabled for the current org. */
  private readonly isInactivityTimeoutEnabled = computed(() =>
    this.canUseService.can(Feature.InactivityTimeout),
  );

  /** Get the `sessionIdleTimeoutSeconds` for the current org. */
  private readonly orgIdleTimeoutConfig = computed<InactivityConfig>(() => {
    const org = this.userState.org()!;
    const timeout = org.sessionIdleTimeoutSeconds ?? AppConstants.DEFAULT_INACTIVITY_TIMEOUT;
    return { timeout, org: { id: org.id, name: org.name }, timestamp: Date.now() };
  });

  private readonly shouldMonitorInactivity = computed(() => {
    return (
      !!this.userState.org() &&
      this.isInactivityTimeoutEnabled() &&
      !this.excludeFromInactivityWarnings()
    );
  });

  constructor() {
    /** (Re)configure inactivity monitoring when relevant inputs change */
    effect(() => {
      const shouldMonitor = this.shouldMonitorInactivity();
      // Only depend on the timeout value, not the entire config object
      const org = this.userState.org();
      const timeoutSeconds =
        org?.sessionIdleTimeoutSeconds ?? AppConstants.DEFAULT_INACTIVITY_TIMEOUT;
      const timeout = (timeoutSeconds - 300) * 1000; // Show warning 5 minutes before timeout

      untracked(() => {
        // Always clean up first
        this.inactivityMonitor.unregisterIdleDetection('app');

        if (shouldMonitor) {
          this.inactivityMonitor.observeIdleDetection('app', timeout);
        }
      });
    });

    /** Reset monitoring when org changes. */
    effect(() => {
      const orgId = this.userState.org()?.id;
      const shouldMonitor = this.shouldMonitorInactivity();

      if (!orgId) {
        return;
      }

      untracked(() => {
        if (shouldMonitor) {
          this.inactivityMonitor.reset('app');
        } else {
          this.inactivityMonitor.unregisterIdleDetection('app');
        }
      });
    });

    /** Show inactivity warning modal when user becomes idle. */
    effect(() => {
      const isIdle = this.inactivityMonitor.isIdle('app')();
      const shouldMonitor = this.shouldMonitorInactivity();

      if (!shouldMonitor || !isIdle || this.modalRef) {
        return;
      }

      untracked(() => {
        this.showInactivityModal();
      });
    });

    // Listen for cross-tab dismiss messages
    if (this.broadcastChannel) {
      this.broadcastChannel.addEventListener('message', (event) => {
        if (event.data?.type === 'dismiss-modal') {
          this.handleCrossTabDismiss();
        }
      });
    }

    // Listen for cross-tab modal show/hide tracking
    if (this.trackingChannel) {
      this.trackingChannel.addEventListener('message', (event) => {
        if (event.data?.type === 'modal-shown') {
          // Another tab showed a modal, increment count
          this.otherTabsWarningCount.update((count) => count + 1);
          this.updateWarningToast();
        } else if (event.data?.type === 'modal-dismissed') {
          // Another tab dismissed a modal, decrement count
          this.otherTabsWarningCount.update((count) => Math.max(0, count - 1));
          this.updateWarningToast();
        }
      });
    }
  }

  restartMonitoring(): void {
    this.modalRef = null;
    this.inactivityMonitor.reset('app');
    // Broadcast dismiss message to other tabs
    this.broadcastDismiss();
    // Broadcast modal dismissed for tracking
    this.broadcastModalDismissed();
  }

  private broadcastDismiss(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage({ type: 'dismiss-modal' });
    }
  }

  private broadcastModalShown(): void {
    if (this.trackingChannel) {
      this.trackingChannel.postMessage({ type: 'modal-shown' });
    }
  }

  private broadcastModalDismissed(): void {
    if (this.trackingChannel) {
      this.trackingChannel.postMessage({ type: 'modal-dismissed' });
    }
  }

  private handleCrossTabDismiss(): void {
    if (this.modalRef) {
      this.modalRef.close(true);
      this.modalRef = null;
    }
    this.inactivityMonitor.reset('app');
    // Broadcast modal dismissed for tracking
    this.broadcastModalDismissed();
  }

  private updateWarningToast(): void {
    const count = this.otherTabsWarningCount();

    // Show toast if there are warnings in other tabs
    if (count > 0) {
      const message =
        count === 1
          ? $localize`There is an Inactivity Warning message open in another Copado Robotic Testing
            tab. Please dismiss it to keep your session active.`
          : $localize`There are Inactivity Warning messages open in other Copado Robotic Testing
            tabs. Please dismiss at least one of the warnings to keep your session active.`;

      if (this.warningCountToast) {
        // Update existing toast
        this.toastService.remove(this.warningCountToast);
      }

      this.warningCountToast = this.toastService.show({
        autohide: false,
        text: message,
        title: $localize`Inactivity Warning`,
        type: BootstrapSuffix.Warning,
      });
    } else if (this.warningCountToast) {
      // Remove toast only if count is 0
      this.toastService.remove(this.warningCountToast);
      this.warningCountToast = null;
    }
  }

  private async showInactivityModal(): Promise<void> {
    if (this.modalRef || !this.inactivityMonitor.isIdle('app')()) return;

    await this.browserNotifications.showNotification({
      body: 'Your Copado Robotic Testing session will timeout due to inactivity.',
      icon: '/favicon.ico',
      tag: 'inactivity-warning',
      title: 'Are you still here?',
    });

    if (!this.inactivityMonitor.isIdle('app')() || this.modalRef) return;

    this.modalRef = this.ngbModal.open(InactivityWarningModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'md',
    });

    const componentInstance: InactivityWarningModalComponent = this.modalRef.componentInstance;
    componentInstance.config.set(this.orgIdleTimeoutConfig());

    // Broadcast that this tab showed a modal
    this.broadcastModalShown();

    this.modalRef.result
      .then((result) => {
        if (result) {
          // User confirmed, restart monitoring and broadcast to other tabs
          this.restartMonitoring();
        } else {
          // Modal was closed without confirmation (timeout), broadcast dismissed
          this.broadcastModalDismissed();
        }
      })
      .finally(() => {
        this.modalRef = null;
      });
  }
}
